import { useEffect, useState } from 'react'
import {
  PlanStatusEnum,
  useGetPlanSummariesQuery,
  FilterDrawer,
  getEmptyTaskFilters,
  getFilterCount,
  getOwnerIdFromSettings,
  Mtds,
  ScreenHeader,
  TopBar,
  AppLayout,
  useAvdeling,
  useFilters,
  useGetOwnerNameQuery,
  useGetTasksQuery
} from 'prove-common'
import {
  filterTaskList,
  getTaskList,
  groupTaskList,
  sortTaskList
} from '../utils/taskHelper'
import TaskFilterForm from '../components/task-filter-form/TaskFilterForm'
import { TaskGroupList } from '../components/task-group-list/TaskGroupList'
import { FilterTags } from '../components/filter-tags/FilterTags'
import { useSearchParams } from 'react-router'
import { AppLogo } from '../components/app-logo/AppLogo'
import { SideBar } from '../components/side-bar/SideBar'

export default function TasksScreen() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tabIndexParam = searchParams.get('tab')
  const settings = useAvdeling()

  // Initialize selectedTabIndex from searchParams or fallback to localStorage or 0
  const [selectedTabIndex, setSelectedTabIndex] = useState(() => {
    const savedTabIndex = localStorage.getItem('localSettings.tasks.tabIndex')
    if (tabIndexParam) return Number(tabIndexParam)
    if (savedTabIndex) return Number(savedTabIndex)
    return 0
  })

  // Persist selectedTabIndex to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(
      'localSettings.tasks.tabIndex',
      selectedTabIndex.toString()
    )
  }, [selectedTabIndex])

  //   const [sortBy, setSortBy] = useState<SortTasksBy>('createdDate')
  const { filters, setFilters, isFiltering } =
    useFilters<PlanStatusEnum>('filtersTasks')
  const ownerIdFilter = filters.ownerId ?? getOwnerIdFromSettings(settings.get)
  const { data: plans } = useGetPlanSummariesQuery()
  const {
    data: tasks,
    isPending: isPendingTasks,
    isFetching: isFetchingTasks,
    isError: isErrorTasks
  } = useGetTasksQuery({
    ownerId: ownerIdFilter == null ? undefined : [ownerIdFilter]
  })
  const {
    data: ownerName,
    isPending: isPendingOwnerName,
    isError: isErrorOwnerName
  } = useGetOwnerNameQuery(ownerIdFilter)

  const uniquePlanIds = Array.from(
    new Set((tasks ?? []).flatMap((task) => task.planIds ?? []))
  )
  const uniqueProductIds = Array.from(
    new Set(
      (tasks ?? [])
        .map((task) => task.analysisType?.sampleType?.productId)
        .filter((id) => id != null)
    )
  )
  const uniqueMatrixIds = Array.from(
    new Set(
      (tasks ?? [])
        .map((task) => task.analysisType?.sampleType?.matrixId)
        .filter((id) => id != null)
    )
  )
  const uniqueSampntTypeIds = Array.from(
    new Set(
      (tasks ?? [])
        .map((task) => task.sampntType?.id)
        .filter((id) => id != null)
    )
  )
  const uniqueSubstanceIds = Array.from(
    new Set(
      (tasks ?? [])
        .flatMap((task) => task.analysisType?.substances ?? [])
        .map((s) => s.substance.id)
        .filter((id) => id != null)
    )
  )
  const uniquePurposeIds = Array.from(
    new Set(
      (tasks ?? []).map((task) => task.purposeId).filter((id) => id != null)
    )
  )
  const uniqueSubPurposeIds = Array.from(
    new Set(
      (tasks ?? []).map((task) => task.subPurposeId).filter((id) => id != null)
    )
  )
  const unfilteredTaskList = getTaskList(tasks ?? [], plans)
  const approvedTaskList = unfilteredTaskList.filter(
    (taskItem) => taskItem.task.analysisTypeApprovalStatus === 'approved'
  )
  const filteredTaskList = filterTaskList(approvedTaskList, filters)
  const sortedTaskList = sortTaskList(filteredTaskList, 'dueDate')
  const groupedTaskList = groupTaskList(sortedTaskList, plans)
  const groupedTaskListNotCompleted = groupedTaskList.filter(
    ({ isCompleted }) => !isCompleted
  )
  const groupedTaskListCompleted = groupedTaskList.filter(
    ({ isCompleted }) => isCompleted
  )
  const taskHitCount =
    selectedTabIndex === 0
      ? groupedTaskListNotCompleted.length
      : groupedTaskListCompleted.length

  useEffect(() => {
    setSearchParams({ tab: selectedTabIndex.toString() }, { replace: true })
  }, [selectedTabIndex])

  const isPending = isPendingTasks || isPendingOwnerName || settings.isPending
  const isError = isErrorTasks || isErrorOwnerName || settings.isError
  const [isFilterClicked, setIsFilterClicked] = useState(false)
  const [isFilterReady, setIsFilterReady] = useState(false)
  const filterCount = getFilterCount(filters)

  const resetFilters = () => {
    setFilters(getEmptyTaskFilters())
  }

  return (
    <AppLayout
      sidebar={<SideBar />}
      isLoading={isPending && !isError}
      isError={isError}
      topBar={<TopBar logo={<AppLogo />} title={'Oppgaver'} />}
    >
      <ScreenHeader
        title={
          isPendingOwnerName ? (
            <Mtds.Skeleton />
          ) : (
            `${ownerName?.name?.[0]?.toUpperCase()}${ownerName?.name?.slice(1)}`
          )
        }
      />
      <Mtds.Grid data-gap='4'>
        <Mtds.Flex data-justify={'space-between'}>
          <Mtds.Togglegroup
            {...({ focusgroup: 'radiogroup' } as Record<string, string>)}
            aria-label='Status for oppgaver'
          >
            <Mtds.Togglegroup.Item
              checked={selectedTabIndex === 0}
              name='task-filter'
              value='oppgaver'
              onChange={() => setSelectedTabIndex(0)}
            >
              Oppgaver ({groupedTaskListNotCompleted.length})
            </Mtds.Togglegroup.Item>
            <Mtds.Togglegroup.Item
              checked={selectedTabIndex === 1}
              name='task-filter'
              value='ferdig'
              onChange={() => setSelectedTabIndex(1)}
            >
              Ferdig ({groupedTaskListCompleted.length})
            </Mtds.Togglegroup.Item>
          </Mtds.Togglegroup>
          <FilterDrawer
            title='Filtrer oppgaver'
            isFiltering={isFiltering}
            applyLabel={`Vis ${taskHitCount} treff`}
            isApplyLoading={isFetchingTasks}
            showApply={isFilterReady}
            filterCount={filterCount}
            onReset={resetFilters}
            onOpen={() => setIsFilterClicked(true)}
          >
            <TaskFilterForm
              filters={filters}
              setFilters={setFilters}
              isEnabled={isFilterClicked}
              uniquePlanIds={uniquePlanIds}
              uniqueProductIds={uniqueProductIds}
              uniqueMatrixIds={uniqueMatrixIds}
              uniqueSampntTypeIds={uniqueSampntTypeIds}
              uniqueSubstanceIds={uniqueSubstanceIds}
              uniquePurposeIds={uniquePurposeIds}
              uniqueSubPurposeIds={uniqueSubPurposeIds}
              onReadyChange={setIsFilterReady}
            />
          </FilterDrawer>
          {/* <Mtds.Button
              popoverTarget="filter-sort-type"
              data-variant={'secondary'}
              data-arrow
          >
            Sorter
          </Mtds.Button>
          <Mtds.Popover as="menu" id="filter-sort-type">
            <li>
              <Mtds.Button
                onClick={() => setSortBy('samplesLeft')}
                aria-pressed={sortBy === 'samplesLeft'}
              >
                Prøver igjen (mange-få)
              </Mtds.Button>
            </li>
            <li>
              <Mtds.Button
                onClick={() => setSortBy('createdDate')}
                aria-pressed={sortBy === 'createdDate'}
              >
                Dato opprettet (synkende)
              </Mtds.Button>
            </li>
          </Mtds.Popover> */}
        </Mtds.Flex>
        <FilterTags
          filters={filters}
          setFilters={setFilters}
          isFiltering={isFiltering}
        />
      </Mtds.Grid>
      <TaskGroupList
        isFiltering={isFiltering}
        taskGroupItems={
          selectedTabIndex === 0
            ? groupedTaskListNotCompleted
            : groupedTaskListCompleted
        }
      />
    </AppLayout>
  )
}
